import { Settings } from "./Settings";
import { GameWorld } from "../../../GameWorld";
import { TIMER } from "../../../../Gameplay";
import { Pool } from "../../../../../Utils/Collections/Pool";
import { ObstacleType, BasicObstacle, eBasicObstacleFlag, ObstacleTypeShadow } from "../../../Obstacle";
import { Bonus as BasicBonus, BONUS_TYPES, eBonusFlag } from "../../../Bonus";
import { eBonusUID } from "../../../../Bonuses/Bonus";
import { BonusManager } from "../../../../Bonuses/BonusManager";
import { eModelAsset, eSfxSprite, eAudioAsset, Game, SfxSprites, eAnimation } from "../../../../../Game";
import { Character } from "../../../../Player/Character";
import { SoundManager } from "../../../../../Utils/SoundManager";



/**
 * Seznam s typy prekazek pro silnici
 */
export const OBSTACLE_TYPES: ObstacleType[] = [
    new ObstacleType(0, eModelAsset.car_0, 0, 0.45, new pc.Vec2(0.42, 0.35), new pc.Vec2(0.01, 0),
        new ObstacleTypeShadow("shadowCar_0", new pc.Vec2(0.6, 0.55), [new pc.Vec3(0.04, 0.01, 0), new pc.Vec3(-0.02, 0.01, 0)])),
    new ObstacleType(1, eModelAsset.car_1, 0, 0.75, new pc.Vec2(0.7, 0.35), new pc.Vec2(0, 0),
        new ObstacleTypeShadow("shadowCar_1", new pc.Vec2(0.8, 0.55), [new pc.Vec3(0.04, 0.01, 0), new pc.Vec3(-0.04, 0.01, 0)])),
];



export class ObstacleController {
    private _obstacle: BasicObstacle;

    get roadSettings(): Settings { return this._roadSettings; }
    private _roadSettings: Settings;

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor(obstacle: BasicObstacle) {
        this._obstacle = obstacle;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    init(roadSettings: Settings): void {
        this._roadSettings = roadSettings;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    updateObstacle(): boolean {
        let res = true;
        let entity = this._obstacle.entity;
        let pos = entity.getLocalPosition();
        let dir = this._roadSettings.obsMoveDir;
        let step = this._roadSettings.obsMoveSpeed * TIMER.deltaS * dir;

        pos.x += step;

        if (dir > 0) {
            if (pos.x > GameWorld.WORLD_WIDTH + this._obstacle.type.width / 2)
                res = false;
        }
        else if (pos.x < -this._obstacle.type.width / 2) {
            res = false;
        }

        entity.setLocalPosition(pos);

        // Aktualizovat kolizni obdelnik
        this._obstacle.colRect.x += step;

        return res;
    }
}



export class Car extends BasicObstacle {
    private _controller: ObstacleController;

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor(type: ObstacleType) {
        super(type, FREE_OBSTACLES[type.uid]);
        this._controller = new ObstacleController(this);

        // Animace
        if (!this._entity.animation) {
            this._entity.addComponent(pc.eCompomentType.animation, {
                assets: [Game.APP.assets.find(type.uid == 0 ? eAnimation.carDrive_0 : eAnimation.carDrive_1, pc.ASSET_ANIMATION)],
                speed: 2,
                loop: true
            });
        }

        // Audio
        if (!this._entity.sound) {
            SoundManager.createSoundComponent(this._entity, {
                positional: true,
                distanceModel: pc.DISTANCE_LINEAR,
                maxDistance: 2
            });

            SoundManager.addSoundSlot(this._entity, eSfxSprite.carEngine, SfxSprites[eSfxSprite.carEngine], Game.APP.assets.find(eAudioAsset.sfx, pc.eAssetType.audio).id);
        }
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    reset(): BasicObstacle {
        this._entity.sound.stop(eSfxSprite.carEngine);

        return super.reset();
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    activate(parent: pc.Entity, z: number, xDir: number, x: number, data?: any): BasicObstacle {
        let roadSettings = data as Settings;

        this._controller.init(roadSettings);

        if (roadSettings.obsMoveDir > 0) {
            this._entity.setLocalEulerAngles(0, 180, 0);
            this._shadow.setLocalPosition(this._type.shadow.offset[1]);
            x = -this._type.width / 2;
        }
        else {
            this._entity.setLocalEulerAngles(0, 0, 0);
            this._shadow.setLocalPosition(this._type.shadow.offset[0]);
            x = GameWorld.WORLD_WIDTH + this._type.width / 2;
        }

        super.activate(parent, z, roadSettings.obsMoveDir, x, data);

        // Animace
        this._entity.animation.play((<pc.Asset[]>this._entity.animation.assets)[0].name);

        // Zvuk motoru
        SoundManager.playSound(this._entity, eSfxSprite.carEngine);

        return this;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    update(): boolean {
        if (!super.update()) {
            this._entity.sound.stop(eSfxSprite.carEngine);
            return false;
        }

        if ((this._flags & eBasicObstacleFlag.destruction) != 0)
            return true;

        return this._controller.updateObstacle();
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    handlePlCollision(plChar: Character): void {
        super.handlePlCollision(plChar);
        this._entity.sound.stop(eSfxSprite.carEngine);
        SoundManager.playSound(plChar.player.soundComponent, eSfxSprite.carHit);
    }
}



/**
 * Pooly s volnymi prekazkami. [index typu prekazky]
 */
export const FREE_OBSTACLES: Pool<BasicObstacle>[] = [
    new Pool<Car>(undefined, 0, true, (): Car => { return new Car(OBSTACLE_TYPES[0]); }),
    new Pool<Car>(undefined, 0, true, (): Car => { return new Car(OBSTACLE_TYPES[1]); }),
];



export class Bonus extends BasicBonus {
    private _controller: ObstacleController;

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    constructor(type: ObstacleType) {
        super(type, FREE_BONUSES[type.uid]);
        this._controller = new ObstacleController(this);
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    activate(parent: pc.Entity, z: number, xDir: number, x: number, data?: any): BasicObstacle {
        let roadSettings = data as Settings;

        this._controller.init(roadSettings);
        super.activate(parent, z, xDir, (roadSettings.obsMoveDir > 0 ? -this._type.width / 2 : GameWorld.WORLD_WIDTH + this._type.width / 2), data);

        return this;
    }

    //-------------------------------------------------------------------------
    //-------------------------------------------------------------------------
    update(): boolean {
        if (!super.update())
            return false;

        if ((this._flags & eBonusFlag.pickupFx) == 0) {
            if (!this._controller.updateObstacle()) {
                BonusManager.instance.incMissedBonuses();
                return false;
            }
        }

        return true;
    }
}



export const FREE_BONUSES: Pool<BasicObstacle>[] = [
    new Pool<Bonus>(undefined, 0, true, (): Bonus => { return new Bonus(BONUS_TYPES[eBonusUID.shield]); }),
    new Pool<Bonus>(undefined, 0, true, (): Bonus => { return new Bonus(BONUS_TYPES[eBonusUID.scoreMultiplier]); }),
]